const CERT_LOGOS = [
  "/cert-cyber-essentials.png",
  "/cert-gdpr.png",
  "/cert-iso27001.png",
  "/cert-soc2.png",
];

export function LegalSecuritySlide({ isActive: _ }: { isActive: boolean }) {
  return (
    <div className="flex flex-col h-full">
      <h2 className="text-[32px] font-light tracking-tight mb-1 font-[family-name:var(--font-heading)]">
        Built for firms where compliance is non-negotiable
      </h2>
      <p className="text-[14px] text-[var(--color-fg-secondary)] leading-relaxed font-light mb-4">
        AI actions are review-based. Professionals remain in control at every step.
      </p>

      {/* Layer 1: Trust Badges — 2×2 grid */}
      <p className="text-[10px] font-[family-name:var(--font-mono)] tracking-widest uppercase text-[var(--color-fg-tertiary)] mb-2">
        Certifications & Standards
      </p>
      <div className="grid grid-cols-2 grid-rows-2 gap-2.5 mb-4" style={{ gridAutoRows: "1fr" }}>
        {[
          { name: "Cyber Essentials", status: "Certified", statusColor: "text-emerald-500 bg-emerald-500/10", desc: "UK government-backed cyber security certification. Independently verified." },
          { name: "GDPR", status: "Aligned", statusColor: "text-[var(--color-accent)] bg-[var(--color-accent)]/10", desc: "Data processing agreements, subject access requests, and lawful basis documentation built in." },
          { name: "ISO 27001", status: "Aligned", statusColor: "text-[var(--color-accent)] bg-[var(--color-accent)]/10", desc: "Information security management controls across infrastructure and incident response." },
          { name: "SOC 2 Type II", status: "Aligned", statusColor: "text-[var(--color-accent)] bg-[var(--color-accent)]/10", desc: "Trust service criteria for security, availability, and confidentiality." },
        ].map((b, i) => (
          <div key={b.name} className="relative overflow-hidden border border-[var(--color-border)] px-4 py-2.5 rounded-sm min-h-[58px]">
            <img
              src={CERT_LOGOS[i]}
              alt=""
              className="absolute -right-2 top-1/2 -translate-y-1/2 h-[110%] w-auto object-contain pointer-events-none"
              style={{ opacity: 0.06 }}
            />
            <div className="flex items-center gap-2 mb-1 relative">
              <span className="text-[13px] font-light font-[family-name:var(--font-heading)]">{b.name}</span>
              <span className={`text-[8px] font-[family-name:var(--font-mono)] tracking-wider uppercase px-1.5 py-0.5 rounded-sm ${b.statusColor}`}>{b.status}</span>
            </div>
            <p className="text-[10px] text-[var(--color-fg-tertiary)] leading-snug relative">{b.desc}</p>
          </div>
        ))}
      </div>

      {/* Layer 2: Technical Security — 3×2 grid */}
      <p className="text-[10px] font-[family-name:var(--font-mono)] tracking-widest uppercase text-[var(--color-fg-tertiary)] mb-2">
        Technical Practices
      </p>
      <div className="grid grid-cols-3 gap-2.5">
        {[
          { title: "AES-256 Encryption", desc: "Data encrypted at rest and in transit. Customer-managed keys available." },
          { title: "UK/EU Data Residency", desc: "All data stored and processed within UK and EU jurisdictions. No offshore processing." },
          { title: "Full Audit Trails", desc: "Every AI action logged. Every decision traceable. Immutable, exportable, regulator-ready." },
          { title: "Tenant & Matter Isolation", desc: "No shared compute, no shared storage, no cross-tenant data access." },
          { title: "Role-Based Access Control", desc: "SSO integration. MFA enforced. Granular permissions per role." },
          { title: "Incident Response", desc: "Documented plan with defined escalation paths. 24-hour notification commitment." },
        ].map((f) => (
          <div key={f.title} className="border border-[var(--color-border)] px-4 py-2.5 rounded-sm">
            <p className="text-[11px] font-medium mb-0.5">{f.title}</p>
            <p className="text-[9px] text-[var(--color-fg-tertiary)] font-[family-name:var(--font-mono)] leading-snug">{f.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
